/**
 * Reverse translation: Ingglish/IPA → source language.
 *
 * Unified pipeline for all languages:
 *   text → extractTokens → mapTokens(reverseWord) → output
 *
 * English reverse: ingglishToArpabet → expandAlternatives → lookupPhonemeKey
 *   (uses frequency-based disambiguation with 5x threshold)
 *
 * Non-English reverse: ingglishToArpabet → stripStress → reverseMap lookup
 *   (uses pre-built reverse map from PhoneDict)
 */

import { lookupPhonemeKey, sortByFrequency, getWordFrequency } from '@ingglish/dictionary';
import { ipaToArpabetClean } from '@ingglish/ipa';
import { applyCasePattern, detectCasePattern, tokenizeIPA } from '@ingglish/normalize';
import {
  expandArpabetAlternatives,
  getFormatHandler,
  ingglishToArpabet,
  needsSeparator,
  registerFormat,
  stripStress,
} from '@ingglish/phonemes';
import { getDictReverseMap } from '../dict-loader';
import type { TranslateOptions } from '../dict-loader';
import type { TranslatedToken } from './forward';
import { translateWord } from './forward';
import type { TranslateResult } from './pipeline';
import { extractTokens, HAS_LETTER, mapTokens } from './pipeline';

// Alternative phoneme interpretation must be this many times more common
// than the primary to override it (prevents "kat" → "cut" while allowing "haloh" → "hello")
const ALT_FREQUENCY_THRESHOLD = 5;

/** Strips leading/trailing IPA notation brackets (/, [, ]) */
const IPA_BRACKETS = /^[/[\]]+|[/[\]]+$/g;

// ============================================================================
// Core Translation Functions
// ============================================================================

/**
 * Translates an IPA word back to English.
 * Returns possible English words sorted by frequency.
 */
export function reverseTranslateIPAWord(ipaWord: string): string[] {
  if (!ipaWord || ipaWord.trim().length === 0) {
    return ipaWord ? [ipaWord] : [];
  }

  const arpabet = ipaToArpabetClean(ipaWord);

  if (!arpabet) {
    return [ipaWord];
  }

  const matches = lookupByArpabet(expandArpabetAlternatives(arpabet));

  if (matches.length === 0) {
    return [ipaWord];
  }

  return matches;
}

/**
 * Translates an Ingglish word back to English.
 * Returns possible English words sorted by frequency, or [] if lookup failed.
 * Non-letter tokens (numbers, punctuation) are returned as-is in a single-element array.
 */
export function reverseTranslateWord(ingglishWord: string): string[] {
  if (!ingglishWord || !HAS_LETTER.test(ingglishWord)) {
    return ingglishWord ? [ingglishWord] : [];
  }

  const casePattern = detectCasePattern(ingglishWord);
  const arpabet = ingglishToArpabet(ingglishWord);
  // ingglishToArpabet always returns non-null for letter input
  /* v8 ignore start */
  if (!arpabet) {
    return [];
  }
  /* v8 ignore stop */

  const variants = expandArpabetAlternatives(arpabet);
  const matches = lookupByArpabet(variants);
  const spelledThisWay = wordsSpelledAs(ingglishWord.toLowerCase(), variants);
  const ranked =
    spelledThisWay.length === 0
      ? matches
      : [...spelledThisWay, ...matches.filter((word) => !spelledThisWay.includes(word))];

  return ranked.map((word) => applyCasePattern(word, casePattern));
}

/**
 * Looks up English words matching an ARPAbet sequence.
 * Tries primary interpretation first. Only considers alternatives (e.g.,
 * AE↔AH ambiguity from schwa='a') when the alternative's best match is
 * overwhelmingly more common than the primary's best match (>5x frequency).
 * This prevents "kat" → "cut" (3.5x) while allowing "haloh" → "hello" (3000x).
 */
function lookupByArpabet(variants: string[][]): string[] {
  const [primary, ...alternatives] = variants;
  // expandArpabetAlternatives always returns at least the primary
  /* v8 ignore start */
  if (!primary) {
    return [];
  }
  /* v8 ignore stop */

  const primaryKey = primary.join(' ');
  const primaryMatches = lookupPhonemeKey(primaryKey);

  if (!primaryMatches || primaryMatches.length === 0) {
    // Primary had no matches — try alternatives
    const allMatches: string[] = [];
    const seen = new Set<string>();
    for (const variant of alternatives) {
      const key = variant.join(' ');
      const matches = lookupPhonemeKey(key);
      if (matches) {
        for (const match of matches) {
          if (!seen.has(match)) {
            seen.add(match);
            allMatches.push(match);
          }
        }
      }
    }
    return allMatches.length > 1 ? sortByFrequency(allMatches) : allMatches;
  }

  // Primary has matches — check if any alternative has a much better match
  const primaryBestFreq = getWordFrequency(primaryMatches[0] ?? '') ?? 0;

  for (const variant of alternatives) {
    const key = variant.join(' ');
    const matches = lookupPhonemeKey(key);
    if (matches && matches.length > 0) {
      const altBestFreq = getWordFrequency(matches[0] ?? '') ?? 0;
      if (altBestFreq > primaryBestFreq * ALT_FREQUENCY_THRESHOLD) {
        // Alternative is overwhelmingly more common — merge and sort
        const allMatches = [...primaryMatches];
        const seen = new Set(primaryMatches);
        for (const match of matches) {
          if (!seen.has(match)) {
            seen.add(match);
            allMatches.push(match);
          }
        }
        return sortByFrequency(allMatches);
      }
    }
  }

  return primaryMatches;
}

/**
 * Returns the candidate words (across every parse variant) whose forward
 * translation is exactly `spelling`, most common first. The phoneme lookup
 * alone can't tell them apart: the reverse dictionary is stress-stripped and
 * holds every CMU variant, and the parse alternatives (AE→AH, ER→EH+R) cover
 * spellings the forward translator never writes for those phonemes.
 */
function wordsSpelledAs(spelling: string, variants: string[][]): string[] {
  const seen = new Set<string>();
  const exact: string[] = [];
  for (const variant of variants) {
    for (const word of lookupPhonemeKey(variant.join(' ')) ?? []) {
      if (!seen.has(word)) {
        seen.add(word);
        if (translateWord(word).toLowerCase() === spelling) {
          exact.push(word);
        }
      }
    }
  }
  return exact.length > 1 ? sortByFrequency(exact) : exact;
}

// ============================================================================
// Unified Reverse Translation
// ============================================================================

// Register reverse handlers for built-in formats
registerFormat('ingglish', {
  reverseText: reverseTranslateIngglishText,
  reverseTextWithMapping: reverseTranslateIngglishTextWithMapping,
});
registerFormat('ipa', {
  reverseText: reverseTranslateIPATextInternal,
  reverseTextWithMapping: reverseTranslateIPATextWithMapping,
});

/**
 * Synchronous reverse translation. For English, the reverse dictionary,
 * frequencies and the forward dictionary must already be loaded; for other
 * languages, the reverse map.
 *
 * All languages go through the same pipeline:
 *   text → extractTokens → mapTokens(reverseWord) → output
 *
 * For non-English languages, the reverse map must have been built by a prior
 * `await reverseTranslate(text, { lang })` call.
 */
export function reverseTranslateSync(text: string, options: TranslateOptions = {}): string {
  const { format = 'ingglish', lang } = options;
  const reverseMap = lang ? getDictReverseMap(lang) : undefined;

  if (reverseMap) {
    // Ingglish output is always Latin script, so use standard extractTokens
    const { preserved, rawTokens } = extractTokens(text);
    return mapTokens(rawTokens, preserved, (w) => reverseLangWordAsResult(w, reverseMap))
      .map((t) => t.translated)
      .join('');
  }

  const handler = getFormatHandler(format);
  if (handler?.reverseText) {
    return handler.reverseText(text);
  }
  return reverseTranslateIngglishText(text);
}

/**
 * Like {@link reverseTranslate}, but returns token-by-token mappings instead of a string.
 * Each token includes the original text, translation, and whether it matched
 * the dictionary. Dictionary must already be loaded.
 */
export function reverseTranslateSyncWithMapping(
  text: string,
  options: TranslateOptions = {}
): TranslatedToken[] {
  const { format = 'ingglish', lang } = options;
  const reverseMap = lang ? getDictReverseMap(lang) : undefined;

  if (reverseMap) {
    const { preserved, rawTokens } = extractTokens(text);
    return mapTokens(rawTokens, preserved, (w) => reverseLangWordAsResult(w, reverseMap));
  }

  const handler = getFormatHandler(format);
  if (handler?.reverseTextWithMapping) {
    return handler.reverseTextWithMapping(text);
  }
  return reverseTranslateIngglishTextWithMapping(text);
}

/**
 * Rejoins words the forward translator split with a hyphen to avoid three
 * identical letters in a row ("eezee-er" is "easier"), when the joined word
 * is in the dictionary. Other hyphens separate compound parts. Words sit at
 * odd indices of `rawTokens`; joining word + "-" + word keeps that parity.
 */
function joinSeparatedWords(rawTokens: string[]): string[] {
  const out: string[] = [];
  for (const token of rawTokens) {
    const left = out.at(-2);
    if (out.length % 2 === 1 && out.at(-1) === '-' && left && needsSeparator(left, token)) {
      const joined = `${left}-${token}`;
      if (reverseTranslateWordAsResult(joined).matched) {
        out.splice(-2, 2, joined);
        continue;
      }
    }
    out.push(token);
  }
  return out;
}

// ============================================================================
// Reverse Translation with Mapping
// ============================================================================

/**
 * Reverse-translate a single Ingglish word back to a non-English language
 * using the pre-built reverse map.
 */
function reverseLangWordAsResult(word: string, reverseMap: Map<string, string[]>): TranslateResult {
  /* v8 ignore start */
  if (!HAS_LETTER.test(word)) {
    return { matched: true, translated: word };
  }
  /* v8 ignore stop */

  const casePattern = detectCasePattern(word);
  const arpabet = ingglishToArpabet(word);
  /* v8 ignore start */
  if (!arpabet) {
    return { matched: false, translated: word };
  }
  /* v8 ignore stop */

  // Try primary interpretation and alternatives (e.g. AE↔AH ambiguity)
  const [primary, ...alternatives] = expandArpabetAlternatives(arpabet);
  /* v8 ignore start */
  if (!primary) {
    return { matched: false, translated: word };
  }
  /* v8 ignore stop */

  const primaryKey = primary.map((p) => stripStress(p)).join(' ');
  let matches = reverseMap.get(primaryKey);

  if (!matches || matches.length === 0) {
    for (const variant of alternatives) {
      const key = variant.map((p) => stripStress(p)).join(' ');
      matches = reverseMap.get(key);
      if (matches && matches.length > 0) {
        break;
      }
    }
  }

  if (matches && matches.length > 0) {
    const result = applyCasePattern(matches[0]!, casePattern);
    return { matched: true, translated: result };
  }

  return { matched: false, translated: word };
}

/**
 * Translates Ingglish text back to English.
 * URLs and emails are preserved unchanged.
 */
function reverseTranslateIngglishText(text: string): string {
  return reverseTranslateIngglishTextWithMapping(text)
    .map((t) => t.translated)
    .join('');
}

/**
 * Ingglish reverse translation with token-by-token mapping.
 */
function reverseTranslateIngglishTextWithMapping(text: string): TranslatedToken[] {
  const { preserved, rawTokens } = extractTokens(text);
  return mapTokens(joinSeparatedWords(rawTokens), preserved, reverseTranslateWordAsResult);
}

/**
 * Translates IPA text back to English, preserving punctuation.
 */
function reverseTranslateIPATextInternal(text: string): string {
  // Strip leading/trailing IPA notation brackets (/, [, ]) but preserve internal punctuation
  const cleanText = text.replaceAll(IPA_BRACKETS, '');
  const tokens = tokenizeIPA(cleanText);

  return tokens
    .map((token) => {
      if (token.isWord) {
        const matches = reverseTranslateIPAWord(token.text);
        return matches[0] ?? token.text;
      }
      return token.text;
    })
    .join('');
}

/**
 * Translates IPA text back to English with token-by-token mapping.
 */
function reverseTranslateIPATextWithMapping(text: string): TranslatedToken[] {
  const cleanText = text.replaceAll(IPA_BRACKETS, '');
  const tokens = tokenizeIPA(cleanText);

  return tokens.map((token) => {
    if (token.isWord) {
      const matches = reverseTranslateIPAWord(token.text);
      const translated = matches[0] ?? token.text;
      return {
        isWord: true,
        matched: translated !== token.text,
        original: token.text,
        translated,
      };
    }
    return { isWord: false, matched: true, original: token.text, translated: token.text };
  });
}

/**
 * Translate a single Ingglish word (with contraction support) returning a
 * TranslateResult for use with the pipeline's mapTokens stage.
 */
function reverseTranslateWordAsResult(word: string): TranslateResult {
  /* v8 ignore start */
  if (!HAS_LETTER.test(word)) {
    return { matched: true, translated: word };
  }
  /* v8 ignore stop */

  if (word.includes("'")) {
    // Handle contractions by splitting on apostrophe
    const parts = word.split("'");
    let allMatched = true;
    const translatedParts = parts.map((p) => {
      // Word tokens can't start/end with an apostrophe since WORD_SPLIT_REGEX
      // only accepts internal ones, so parts are never empty; defensive guard
      /* v8 ignore start */
      if (!p) {
        return '';
      }
      /* v8 ignore stop */
      const matches = reverseTranslateWord(p);
      if (matches[0]) {
        return matches[0];
      }
      allMatched = false;
      return p;
    });
    return { matched: allMatched, translated: translatedParts.join("'") };
  }

  const matches = reverseTranslateWord(word);
  if (matches[0]) {
    return { matched: true, translated: matches[0] };
  }
  return { matched: false, translated: word };
}
